import React, { useState, useEffect } from "react";
import { FaUsersCog, FaUserShield, FaBuilding, FaChartBar, FaSignOutAlt } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import { onAuthStateChanged } from "firebase/auth";
import { auth } from "./firebase";
import { API_BASE } from "./config";


function AdminPage({ token, role }) {
  const [activeTab, setActiveTab] = useState("dashboard");
  const navigate = useNavigate();




  if (role !== "Admin") {
    return (
      <div className="flex flex-col items-center justify-center h-screen bg-gray-50 text-gray-800 px-6">
        <div className="bg-white p-10 rounded-xl shadow-lg text-center max-w-md w-full">
          <div className="text-5xl mb-4 text-red-500">🚫</div>
          <h1 className="text-2xl font-bold mb-2">Admin Access Required</h1>
          <p className="text-gray-600 mb-6">
            Sorry, this page is restricted to admin users. If you believe this is a mistake, please contact your system administrator.
          </p>
          <button
            onClick={() => window.location.href = "/"}
            className="bg-[#179D68] hover:bg-[#127b52] text-white px-6 py-2 rounded-lg transition shadow"
          >
            Return to Login
          </button>
        </div>
      </div>
    );
  }

  const tabs = [
    { key: "users", label: "Users", icon: <FaUsersCog size={16} /> },
    { key: "roles", label: "Roles", icon: <FaUserShield size={16} /> },
    { key: "companies", label: "Companies", icon: <FaBuilding size={16} /> },
    { key: "dashboard", label: "Dashboards", icon: <FaChartBar size={16} /> }
  ];

  const sectionHeading = {
    users: "Manage Users",
    roles: "Manage Roles",
    companies: "Manage Companies",
    dashboard: "Dashboard Access Control"
  }[activeTab];

  const renderTabContent = () => {
    switch (activeTab) {
      case "users": return <UsersSection token={token} />;
      case "roles": return <RolesSection token={token} />;
      case "companies": return <CompaniesSection token={token} />;
      case "dashboard": return <DashboardAccessSection token={token} />;
      default: return null;
    }
  };

return (
  <div className="flex flex-col md:flex-row min-h-screen font-sans bg-gradient-to-br from-[#2baf6f] via-[#179D68] to-[#209465] text-white">
    {/* Left Branding Panel (30%) */}
    <div className="w-full md:w-[30%] flex flex-col justify-center items-center p-8 md:p-10 relative">
      {/* Responsive Header */}
      <div className="absolute top-4 left-4 md:top-6 md:left-6 flex flex-col md:flex-row items-start md:items-center gap-2 md:gap-3 max-w-xs md:max-w-none">
        <div className="bg-white/20 text-white font-bold text-lg md:text-xl px-3 py-1 rounded-md shadow-md border border-white/30 tracking-wide">
          DR
        </div>
      </div>

      <div className="text-center px-4 md:px-6 mt-12 md:mt-0">
        <h1 className="text-3xl md:text-5xl font-bold leading-tight mb-4 drop-shadow">
          {sectionHeading}
        </h1>
        <p className="text-base md:text-xl text-white/90 max-w-md mx-auto">
          {activeTab === "dashboard"
            ? "Grant or restrict access to dashboards by company or role."
            : activeTab === "users"
            ? "Invite or remove users from the portal."
            : activeTab === "companies"
            ? "Manage companies and metadata."
            : "Define roles available to users."}
        </p>
      </div>
    </div>

    {/* Divider - hidden on mobile */}
    <div className="hidden md:block absolute top-0 bottom-0 left-[30%] -translate-x-1/2 w-1 bg-white z-10" />

    {/* Right Panel (70%) — stretch children & reduce padding a bit */}
    <div className="w-full md:w-[70%] bg-[#e7ece8] text-gray-900 relative flex flex-col items-stretch p-4 md:p-8 overflow-y-auto">
      {/* Horizontal Pill Menu */}
      <div className="flex flex-wrap gap-3 justify-center mb-6 md:mb-8">
        {tabs.map((tab) => {
          const isActive = activeTab === tab.key;
          return (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={`flex items-center gap-2 px-4 py-2 rounded-full font-semibold text-sm shadow-md transition-all ${
                isActive
                  ? "bg-[#179D68] text-white shadow-lg"
                  : "bg-white text-[#179D68] hover:bg-gray-100"
              }`}
              style={{
                WebkitBackdropFilter: "blur(12px)",
                backdropFilter: "blur(12px)",
                transition: "all 0.3s ease-in-out",
                border: "1px solid rgba(255, 255, 255, 0.3)",
              }}
            >
              <div
                className={`w-6 h-6 flex items-center justify-center rounded-full ${
                  isActive ? "bg-white text-[#179D68]" : "text-[#179D68]"
                }`}
              >
                {tab.icon}
              </div>
              {tab.label}
            </button>
          );
        })}

        {/* Exit Admin Button */}
        <button
          onClick={() => (window.location.href = "/")}
          className="flex items-center px-4 py-2 rounded-full font-semibold text-sm shadow-md border border-white/30 bg-white text-[#179D68] hover:bg-gray-100 backdrop-blur-md"
          style={{
            WebkitBackdropFilter: "blur(12px)",
            backdropFilter: "blur(12px)",
            transition: "all 0.3s ease-in-out",
          }}
        >
          <FaSignOutAlt className="mr-2" />
          Exit Admin
        </button>
      </div>

      {/* Content now truly full width */}
      <div className="w-full">{renderTabContent()}</div>
    </div>
  </div>
);
}

export default AdminPage;



// Section Stubs
function UsersSection({ token }) {
  const [inviteData, setInviteData] = useState({
    email: "",
    role: "",
    company_id: "",
    client_name: "",
    director: "",
    state: "",
    cx: ""
  });

  const [roles, setRoles] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [toast, setToast] = useState("");

  const [search, setSearch] = useState("");

  const filteredUsers = users.filter((u) =>
    `${u.email} ${u.role} ${u.company_name} ${u.cx ?? ""}`.toLowerCase().includes(search.toLowerCase())
  );

  const [editingUserId, setEditingUserId] = useState(null);
  const [editedUserData, setEditedUserData] = useState({});

  const startEditing = (user) => {
    setEditingUserId(user.user_id);
    setEditedUserData({
      role: user.role,
      company_id: user.company_id || "",
      client_name: user.client_name || "",
      director: user.director || "",
      state: user.state || "",
      cx: user.cx || "no"
    });
  };

  const cancelEditing = () => {
    setEditingUserId(null);
    setEditedUserData({});
  };

  const saveUserChanges = async (user_id) => {
    try {
      const response = await fetch(`${API_BASE}/update-user`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: token,
        },
        body: JSON.stringify({ user_id, ...editedUserData }),
      });

      if (response.ok) {
        setToast("User updated successfully.");
        cancelEditing();
        fetchUsersOnly();
      } else {
        const err = await response.json();
        alert(err.detail || "Failed to update user.");
      }
    } catch (err) {
      console.error("Update error:", err);
      alert("Error updating user.");
    }
  };

  const fetchUsersData = async () => {
    try {
      const [rolesRes, companiesRes, usersRes] = await Promise.all([
        fetch(`${API_BASE}/roles`, { headers: { Authorization: token } }),
        fetch(`${API_BASE}/companies`, { headers: { Authorization: token } }),
        fetch(`${API_BASE}/users`, { headers: { Authorization: token } }),
      ]);
      setRoles(await rolesRes.json());
      setCompanies(await companiesRes.json());
      setUsers(await usersRes.json());
    } catch (err) {
      console.error("Error fetching user-related data:", err);
    }
  };

  useEffect(() => {
    fetchUsersData();
  }, [token]);

  const fetchUsersOnly = async () => {
    try {
      const [usersRes] = await Promise.all([
        fetch(`${API_BASE}/users`, { headers: { Authorization: token } }),
      ]);
      setUsers(await usersRes.json());
    } catch (err) {
      console.error("Error fetching user-related data:", err);
    }
  };

  useEffect(() => {
    fetchUsersOnly();
  }, [token]);

  const handleInvite = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE}/invite-user`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: token },
        body: JSON.stringify(inviteData),
      });
      if (!res.ok) throw new Error("Failed to invite user");

      setInviteData({
        email: "",
        role: "",
        company_id: "",
        client_name: "",
        director: "",
        state: "",
        cx: ""
      });

      setToast("✅ User invited!");
      fetchUsersData();
      setTimeout(() => setToast(""), 5000);
    } catch (err) {
      alert(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteUser = async (user_id, email) => {
    if (!window.confirm(`Are you sure you want to delete ${email}?`)) return;

    try {
      const res = await fetch(`${API_BASE}/users/${user_id}`, {
        method: "DELETE",
        headers: { Authorization: token },
      });

      if (!res.ok) throw new Error("Failed to delete user");

      setUsers((prev) => prev.filter((u) => u.user_id !== user_id));
      fetchUsersData();
      setToast(`🗑️ User ${email} deleted`);
      setTimeout(() => setToast(""), 5000);
    } catch (err) {
      alert(`Error deleting user: ${err.message}`);
    }
  };

  return (
    <div className="space-y-10 w-full">
      {toast && (
        <div className="bg-green-500 text-white p-3 rounded-md shadow w-full">{toast}</div>
      )}

      {/* Invite User Form (full width card) */}
      <form onSubmit={handleInvite} className="bg-white p-6 rounded-lg shadow-md space-y-6 w-full">
        <h2 className="text-xl font-semibold text-gray-800">Invite User</h2>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">User Email</label>
            <input
              type="email"
              value={inviteData.email}
              onChange={(e) => setInviteData({ ...inviteData, email: e.target.value })}
              className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#179D68]"
              placeholder="Enter user email"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">User Role</label>
            <select
              value={inviteData.role}
              onChange={(e) => setInviteData({ ...inviteData, role: e.target.value })}
              className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#179D68]"
              required
            >
              <option value="">Select Role</option>
              {roles.map((role) => (
                <option key={role} value={role}>{role}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Company</label>
            <select
              value={inviteData.company_id}
              onChange={(e) => setInviteData({ ...inviteData, company_id: e.target.value })}
              className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#179D68]"
              required
            >
              <option value="">Select Company</option>
              {companies.map((c) => (
                <option key={c.company_id} value={c.company_id}>{c.company_name}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Client Name</label>
            <input
              type="text"
              value={inviteData.client_name}
              onChange={(e) => setInviteData({ ...inviteData, client_name: e.target.value })}
              className="w-full p-2 border border-gray-300 rounded-md"
              placeholder="Optional"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Director</label>
            <input
              type="text"
              value={inviteData.director}
              onChange={(e) => setInviteData({ ...inviteData, director: e.target.value })}
              className="w-full p-2 border border-gray-300 rounded-md"
              placeholder="Optional"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Location</label>
            <input
              type="text"
              value={inviteData.state}
              onChange={(e) => setInviteData({ ...inviteData, state: e.target.value })}
              className="w-full p-2 border border-gray-300 rounded-md"
              placeholder="Optional"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">CX Access</label>
            <select
              value={inviteData.cx}
              onChange={(e) => setInviteData({ ...inviteData, cx: e.target.value })}
              className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#179D68]"
              required
            >
              <option value="">CX (Yes / No)</option>
              <option value="yes">Yes</option>
              <option value="no">No</option>
            </select>
          </div>
        </div>

        <button
          type="submit"
          disabled={loading}
          className="bg-[#179D68] hover:bg-[#127b52] text-white px-6 py-2 rounded-md font-semibold transition"
        >
          {loading ? "Inviting..." : "Invite User"}
        </button>
      </form>

      {/* Current Users (full width card) */}
      <div className="bg-white p-6 rounded-lg shadow-md w-full">
        <h2 className="text-xl font-semibold text-gray-800 mb-4">Current Users</h2>

        <input
          type="text"
          placeholder="Search by email, role, company, and etc."
          className="w-full mb-4 p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#179D68]"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />

        {/* Scroll container — horizontal when needed, vertical up to 60vh */}
        <div className="overflow-auto max-h-[60vh] w-full">
          <table className="w-full min-w-[1400px] table-auto text-sm border rounded-md">
            <thead className="bg-gray-100 sticky top-0 z-10 shadow">
              <tr className="[&>th]:px-2 [&>th]:py-2 [&>th]:border">
                <th className="text-left min-w-[280px]">Email</th>
                <th className="text-left min-w-[130px] whitespace-nowrap">Role</th>
                <th className="text-left min-w-[240px]">Company</th>
                <th className="text-left min-w-[220px]">Client</th>
                <th className="text-left min-w-[180px]">Director</th>
                <th className="text-left min-w-[160px]">Location</th>
                <th className="text-left min-w-[110px] whitespace-nowrap">CX</th>
                <th className="text-left min-w-[160px]">Actions</th>
              </tr>
            </thead>

            <tbody>
              {filteredUsers.map((u) => (
                <tr key={u.user_id ?? u.email} className="border-t hover:bg-gray-50">
                  {/* Email */}
                  <td className="px-2 py-1 border align-middle">{u.email}</td>

                  {editingUserId === u.user_id ? (
                    <>
                      {/* Role */}
                      <td className="px-2 py-1 border">
                        <select
                          value={editedUserData.role}
                          onChange={(e) =>
                            setEditedUserData({ ...editedUserData, role: e.target.value })
                          }
                          className="w-full min-w-[8rem] border rounded px-2 py-1"
                        >
                          {roles.map((role) => (
                            <option key={role} value={role}>
                              {role}
                            </option>
                          ))}
                        </select>
                      </td>

                      {/* Company */}
                      <td className="px-2 py-1 border">
                        <select
                          value={editedUserData.company_id}
                          onChange={(e) =>
                            setEditedUserData({ ...editedUserData, company_id: e.target.value })
                          }
                          className="w-full min-w-[14rem] border rounded px-2 py-1"
                        >
                          <option value="">Select Company</option>
                          {companies.map((c) => (
                            <option key={c.company_id} value={c.company_id}>
                              {c.company_name}
                            </option>
                          ))}
                        </select>
                      </td>

                      {/* Client */}
                      <td className="px-2 py-1 border">
                        <input
                          value={editedUserData.client_name}
                          onChange={(e) =>
                            setEditedUserData({ ...editedUserData, client_name: e.target.value })
                          }
                          className="w-full min-w-[13rem] border rounded px-2 py-1"
                        />
                      </td>

                      {/* Director */}
                      <td className="px-2 py-1 border">
                        <input
                          value={editedUserData.director ?? ""}
                          onChange={(e) =>
                            setEditedUserData((prev) => ({ ...prev, director: e.target.value }))
                          }
                          className="w-full min-w-[11rem] border rounded px-2 py-1"
                        />
                      </td>

                      {/* Location */}
                      <td className="px-2 py-1 border">
                        <input
                          value={editedUserData.state ?? ""}
                          onChange={(e) =>
                            setEditedUserData((prev) => ({ ...prev, state: e.target.value }))
                          }
                          className="w-full min-w-[10rem] border rounded px-2 py-1"
                        />
                      </td>

                      {/* CX (edit) */}
                      <td className="px-2 py-1 border whitespace-nowrap">
                        <select
                          value={editedUserData.cx ?? "no"}
                          onChange={(e) =>
                            setEditedUserData((prev) => ({ ...prev, cx: e.target.value }))
                          }
                          className="w-full min-w-[7rem] border rounded px-2 py-1"
                        >
                          <option value="yes">Yes</option>
                          <option value="no">No</option>
                        </select>
                      </td>

                      {/* Actions */}
                      <td className="px-2 py-1 border">
                        <div className="flex gap-2">
                          <button
                            onClick={() => saveUserChanges(u.user_id)}
                            className="text-green-600 hover:underline"
                          >
                            Save
                          </button>
                          <button onClick={cancelEditing} className="text-gray-500 hover:underline">
                            Cancel
                          </button>
                        </div>
                      </td>
                    </>
                  ) : (
                    <>
                      <td className="px-2 py-1 border whitespace-nowrap">{u.role}</td>
                      <td className="px-2 py-1 border">{u.company_name}</td>
                      <td className="px-2 py-1 border">{u.client_name}</td>
                      <td className="px-2 py-1 border">{u.director}</td>
                      <td className="px-2 py-1 border">{u.state}</td>
                      <td className="px-2 py-1 border whitespace-nowrap">{u.cx ?? "no"}</td>
                      <td className="px-2 py-1 border">
                        <div className="flex gap-3">
                          <button
                            onClick={() => startEditing(u)}
                            className="text-blue-600 hover:underline"
                          >
                            Edit
                          </button>
                          <button
                            onClick={() => handleDeleteUser(u.user_id, u.email)}
                            className="text-red-600 hover:underline"
                          >
                            Remove
                          </button>
                        </div>
                      </td>
                    </>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}



function RolesSection({ token }) {
  const [roles, setRoles] = useState([]);
  const [newRole, setNewRole] = useState("");
  const [toast, setToast] = useState("");
  const [hasAdminAccess, setHasAdminAccess] = useState(true);

  const [search, setSearch] = useState("");

// Filtered roles list
const filteredRoles = roles.filter((role) =>
  role?.toLowerCase().includes(search.toLowerCase())
);





  // Fetch existing roles
  const fetchRoles = async () => {
    try {
      const res = await fetch(`${API_BASE}/roles`, {
        headers: { Authorization: token },
      });
      const data = await res.json();
      setRoles(data);
    } catch (err) {
      console.error("Failed to fetch roles", err);
    }
  };


  // Add role
  const handleAddRole = async (e) => {
  e.preventDefault();
  if (!newRole.trim()) return;

  try {
    const res = await fetch(`${API_BASE}/roles`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: token,
      },
      body: JSON.stringify({ role_name: newRole }),
    });

    if (!res.ok) throw new Error("Failed to add role");

    setToast("✅ Role added successfully");
    setTimeout(() => setToast(""), 5000);
    setNewRole("");
    fetchRoles(); // Refresh the roles list
  } catch (err) {
    alert(err.message);
  }
};

  // Delete role
const handleDeleteRole = async (roleName) => {
  if (!window.confirm(`Delete role "${roleName}"?`)) return;

  try {
    const res = await fetch(`${API_BASE}/user_roles/${encodeURIComponent(roleName)}`, {
      method: "DELETE",
      headers: { Authorization: token },
    });

    if (!res.ok) throw new Error("Failed to delete role");

    setToast(`🗑️ Role "${roleName}" deleted`);
    setTimeout(() => setToast(""), 5000);
    fetchRoles();
  } catch (err) {
    alert(err.message);
  }
};


  useEffect(() => {
    fetchRoles();
  }, [token]);

  return (
    <div className="bg-white p-6 rounded shadow text-gray-800">
      <h2 className="text-lg font-semibold mb-4">User Roles</h2>

      {toast && (
        <div className="bg-green-100 text-green-800 p-2 rounded mb-4">{toast}</div>
      )}

      {/* Add Role Form */}
      <form onSubmit={handleAddRole} className="flex gap-2 mb-6">
        <input
          type="text"
          placeholder="Enter new role"
          value={newRole}
          onChange={(e) => setNewRole(e.target.value)}
          className="border p-2 rounded flex-1"
        />
        <button
          type="submit"
          className="bg-[#179D68] hover:bg-[#127b52] text-white px-4 py-2 rounded"
        >
          Add Role
        </button>
      </form>

    {/* Roles Table */}
    <input
      type="text"
      placeholder="Search roles..."
      value={search}
      onChange={(e) => setSearch(e.target.value)}
      className="border p-2 rounded mb-4 w-full"
    />

    <div className="overflow-x-auto max-h-[300px] overflow-y-auto">
      <table className="w-full table-auto text-left border">
        <thead className="bg-gray-100 sticky top-0 z-10">
          <tr>
            <th className="p-2 border">Role Name</th>
            <th className="p-2 border">Actions</th>
          </tr>
        </thead>
        <tbody>
          {filteredRoles.map((role, idx) => (
            <tr key={idx} className="border-t">
              <td className="p-2 border">{role}</td>
              <td className="p-2 border">
                <button
                  onClick={() => handleDeleteRole(role)}
                  className="text-red-600 hover:underline"
                >
                  Remove
                </button>
              </td>
            </tr>
          ))}
          {filteredRoles.length === 0 && (
            <tr>
              <td className="p-2 border" colSpan={2}>
                No roles found.
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>

    </div>
  );
}




function CompaniesSection({ token }) {
  const [companies, setCompanies] = useState([]);
  const [newCompanyName, setNewCompanyName] = useState("");
  const [loading, setLoading] = useState(false);
  const [toast, setToast] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [hasAdminAccess, setHasAdminAccess] = useState(true);


  useEffect(() => {
    const fetchCompanies = async () => {
      try {
        const res = await fetch(`${API_BASE}/companies`, {
          headers: { Authorization: token },
        });
        const data = await res.json();
        setCompanies(Array.isArray(data) ? data : []);
      } catch (err) {
        console.error("Error fetching companies:", err);
      }
    };

    fetchCompanies();
  }, [token]);


  const handleAddCompany = async (e) => {
    e.preventDefault();
    if (!newCompanyName.trim()) return;

    setLoading(true);
    try {
      const res = await fetch(`${API_BASE}/companies`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: token,
        },
        body: JSON.stringify({ company_name: newCompanyName }),
      });

      if (!res.ok) throw new Error("Failed to add company");

      const added = await res.json();
      setCompanies([...companies, added]);
      setNewCompanyName("");
      setToast(`✅ Added ${added.company_name}`);
      setTimeout(() => setToast(""), 3000);
    } catch (err) {
      alert(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteCompany = async (company_id) => {
    if (!window.confirm("Delete this company?")) return;

    try {
      const res = await fetch(`${API_BASE}/delete-company`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: token,
        },
        body: JSON.stringify({ company_id }),
      });

      if (!res.ok) throw new Error("Failed to delete company");

      setCompanies((prev) => prev.filter((c) => c.company_id !== company_id));
      setToast("🗑️ Company removed");
      setTimeout(() => setToast(""), 3000);
    } catch (err) {
      alert(err.message);
    }
  };

  return (
    <div className="bg-white p-6 rounded shadow text-gray-800">
      <h2 className="text-xl font-semibold mb-4">Manage Companies</h2>

      {toast && (
        <div className="mb-4 bg-green-100 text-green-800 px-4 py-2 rounded">
          {toast}
        </div>
      )}

      <form onSubmit={handleAddCompany} className="mb-6">
        <label className="block mb-2 font-medium">New Company Name</label>
        <input
          type="text"
          value={newCompanyName}
          onChange={(e) => setNewCompanyName(e.target.value)}
          className="w-full px-4 py-2 border border-gray-300 rounded mb-4"
          placeholder="e.g., VIVBI Analytics Inc."
          required
        />
        <button
          type="submit"
          className="bg-[#179D68] hover:bg-[#127b52] text-white px-4 py-2 rounded"
          disabled={loading}
        >
          {loading ? "Adding..." : "Add Company"}
        </button>
      </form>

      {/* Search bar */}
      <input
        type="text"
        placeholder="Search companies..."
        value={searchQuery}
        onChange={(e) => setSearchQuery(e.target.value)}
        className="mb-4 px-4 py-2 w-full border rounded"
      />

      {/* Scrollable table */}
      <div className="max-h-96 overflow-y-auto border rounded">
        <table className="w-full table-auto text-left">
          <thead className="bg-gray-100 sticky top-0">
            <tr>
              <th className="p-2 border">Company Name</th>
              <th className="p-2 border">Actions</th>
            </tr>
          </thead>
          <tbody>
            {companies
              .filter((c) =>
                c.company_name.toLowerCase().includes(searchQuery.toLowerCase())
              )
              .map((company) => (
                <tr key={company.company_id}>
                  <td className="p-2 border">{company.company_name}</td>
                  <td className="p-2 border">
                    <button
                      onClick={() => handleDeleteCompany(company.company_id)}
                      className="text-red-600 hover:underline"
                    >
                      Remove
                    </button>
                  </td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}



function DashboardAccessSection({ token }) {
  const [formData, setFormData] = useState({
    company_id: "",
    dashboard_name: "",
    dashboard_url: "",
    role: "",
  });

  const [accessControls, setAccessControls] = useState([]);
  const [roles, setRoles] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(false);
  const [toast, setToast] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [hasAdminAccess, setHasAdminAccess] = useState(true);



  // Fetch roles and companies on load
  useEffect(() => {
    const fetchData = async () => {
      try {
        const [rolesRes, companiesRes, accessRes] = await Promise.all([
          fetch(`${API_BASE}/roles`, {
            headers: { Authorization: token },
          }),
          fetch(`${API_BASE}/companies`, {
            headers: { Authorization: token },
          }),
          fetch(`${API_BASE}/access-control`, {
            headers: { Authorization: token },
          }),
        ]);

        setRoles(await rolesRes.json());
        setCompanies(await companiesRes.json());
        setAccessControls(await accessRes.json());
      } catch (err) {
        console.error("Error loading dashboard access data:", err);
      }
    };

    fetchData();
  }, [token]);

const handleAddAccess = async (e) => {
  e.preventDefault();
  setLoading(true);
  try {
    const res = await fetch(`${API_BASE}/access-control`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: token,
      },
      body: JSON.stringify({
        ...formData,
        role: formData.role === "all" ? null : formData.role || null,
      }),
    });

    if (!res.ok) throw new Error("Failed to add dashboard access");

    // ✅ Fetch updated access controls from backend
    const refresh = await fetch(`${API_BASE}/access-control`, {
      headers: { Authorization: token },
    });
    const updated = await refresh.json();
    setAccessControls(updated);

    setFormData({
      company_id: "",
      dashboard_name: "",
      dashboard_url: "",
      role: "",
    });

    setToast("✅ Dashboard access added!");
    setTimeout(() => setToast(""), 3000);
  } catch (err) {
    alert(err.message);
  } finally {
    setLoading(false);
  }
};



const handleDeleteAccess = async (entry) => {
  if (!window.confirm("Are you sure you want to delete this access rule?")) return;

  try {
    await fetch(`${API_BASE}/delete-access-control`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: token,
      },
      body: JSON.stringify({
        company_id: entry.company_id || null,
        dashboard_name: entry.dashboard_name,
        dashboard_url: entry.dashboard_url,
        role: entry.role || null,
      }),
    });

    const refreshed = await fetch(`${API_BASE}/access-control`, {
      headers: { Authorization: token },
    });
    setAccessControls(await refreshed.json());

    setToast("🗑️ Access rule deleted successfully.");
    setTimeout(() => setToast(""), 3000);
  } catch (err) {
    alert("Error deleting access rule.");
    console.error(err);
  }
};


if (!hasAdminAccess) {
  return (
    <div className="p-8 text-center text-red-600 font-semibold text-xl">
      Admin Access Required
    </div>
  );
}

  return ( 
    
    <div className="bg-white p-6 rounded shadow text-gray-800">
      <h2 className="text-xl font-semibold mb-4">Dashboard Access Control</h2>
      

      {toast && (
        <div className="mb-4 bg-green-100 text-green-800 px-4 py-2 rounded">
          {toast}
        </div>
      )}

      <form onSubmit={handleAddAccess} className="mb-6">
        <div className="mb-4">
          <label className="block font-medium mb-1">Company</label>
          <select
            value={formData.company_id}
            onChange={(e) =>
              setFormData({
                ...formData,
                company_id: e.target.value === "ALL" ? null : e.target.value,
              })
            }
            className="w-full border px-4 py-2 rounded"
            required
          >
            <option value="">Select a company</option>
            <option value="ALL">All Companies</option>
            {companies.map((c) => (
              <option key={c.company_id} value={c.company_id}>
                {c.company_name}
              </option>
            ))}
          </select>
        </div>

        <div className="mb-4">
          <label className="block font-medium mb-1">Dashboard Name</label>
          <input
            type="text"
            value={formData.dashboard_name}
            onChange={(e) =>
              setFormData({ ...formData, dashboard_name: e.target.value })
            }
            className="w-full border px-4 py-2 rounded"
            required
          />
        </div>

        <div className="mb-4">
          <label className="block font-medium mb-1">Dashboard ID</label>
          <input
            type="text"
            value={formData.dashboard_url}
            onChange={(e) =>
              setFormData({ ...formData, dashboard_url: e.target.value })
            }
            className="w-full border px-4 py-2 rounded"
            required
          />
        </div>

        <div className="mb-4">
          <label className="block font-medium mb-1">Role</label>
          <select
            value={formData.role}
            onChange={(e) =>
              setFormData({ ...formData, role: e.target.value })
            }
            className="w-full border px-4 py-2 rounded"
            required
          >
            <option value="">Select role</option>
            <option value="all">All Roles</option>
            {roles.map((r) => (
              <option key={r} value={r}>
                {r}
              </option>
            ))}
          </select>
        </div>

        <button
          type="submit"
          className="bg-[#179D68] hover:bg-[#127b52] text-white px-4 py-2 rounded"
          disabled={loading}
        >
          {loading ? "Adding..." : "Add Access"}
        </button>
      </form>

{/* Table of access rules */}
<h3 className="text-lg font-semibold mb-2">Existing Access Rules</h3>

<input
  type="text"
  placeholder="Search dashboards..."
  value={searchQuery}
  onChange={(e) => setSearchQuery(e.target.value)}
  className="mb-4 px-4 py-2 w-full border rounded"
/>

<div className="overflow-y-auto max-h-[400px]">
  <table className="w-full border text-left">
    <thead>
      <tr className="bg-gray-100">
        <th className="p-2 border">Company</th>
        <th className="p-2 border">Dashboard</th>
        <th className="p-2 border">URL</th>
        <th className="p-2 border">Role</th>
      </tr>
    </thead>
    <tbody>
  {accessControls
    .filter((entry) =>
      `${entry.company_name} ${entry.dashboard_name} ${entry.role}`
        .toLowerCase()
        .includes(searchQuery.toLowerCase())
    )
    .map((entry, idx) => (
      <tr key={idx} className="border-t">
        <td className="p-2 border">{entry.company_name || "All"}</td>
        <td className="p-2 border">{entry.dashboard_name}</td>
        <td className="p-2 border">
          <a
            href={entry.dashboard_url}
            target="_blank"
            rel="noreferrer"
            className="text-blue-600 underline"
          >
            View
          </a>
        </td>
        <td className="p-2 border">{entry.role || "All Roles"}</td>
        <td className="p-2 border">
          <button
            onClick={() => handleDeleteAccess(entry)}
            className="text-red-600 hover:underline"
          >
            Delete
          </button>
        </td>
      </tr>
    ))}
</tbody>
  </table>
</div>
</div>
);
}






